import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

export interface UpdateSnapshotRetentionResult {
    retentionLimit: number;
    historyCount: number;
}

/** Lowest retention value the backend accepts (see MigrationsController validation). */
export const MIN_RETENTION_LIMIT = 1;
/** Highest retention value the backend accepts (see MigrationsController validation). */
export const MAX_RETENTION_LIMIT = 365;

/**
 * Updates the number of snapshots retained for a recurring snapshot migration.
 * Calls PATCH /api/migrations/{id}/retention. The backend trims older history
 * entries immediately when the new limit is lower than the current count.
 */
const updateSnapshotRetention = async (
    bearerToken: string,
    migrationId: string,
    retentionLimit: number
): Promise<UpdateSnapshotRetentionResult> => {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${migrationId}/retention`;

    try {
        const response = await authenticatedFetch(url, {
            method: 'PATCH',
            headers: {
                ...getV2AuthHeaders(bearerToken),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ retentionLimit })
        });

        if (!response.ok) {
            logger.error('Update snapshot retention API returned error', {
                status: response.status,
                statusText: response.statusText,
                migrationId,
                url
            });
            throw new Error(`Error updating retention: ${response.status} ${response.statusText}`);
        }

        const data: UpdateSnapshotRetentionResult = await response.json();
        logger.info('Snapshot retention updated successfully', {
            migrationId,
            retentionLimit: data.retentionLimit,
            historyCount: data.historyCount
        });
        return data;
    } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        logger.error('Failed to update snapshot retention', { error: msg, migrationId, url });
        throw error;
    }
};

export default updateSnapshotRetention;
